import { Apple, RotateCw, Volume1, Leaf, BookOpen, Droplets } from "lucide-react";
import { IMG } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

const Card = ({ testId, className = "", children, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <div data-testid={testId} className="group relative h-full overflow-hidden rounded-[28px] border border-[#E3D9CD] bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(120,60,20,0.35)] sm:p-9">
      {children}
    </div>
  </Reveal>
);

const Icon = ({ icon: I, dark }) => (
  <span className={`mb-6 inline-flex h-11 w-11 items-center justify-center rounded-full ${dark ? "bg-white/10 text-[#E0A030]" : "bg-[#F2EAE1] text-[#C85A32]"}`}><I size={20} /></span>
);

export const Bento = () => (
  <section id="features" data-testid="bento-grid" className="bg-[#FDFBF7] py-20 sm:py-28">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <Reveal className="mb-14 max-w-3xl">
        <p className="eyebrow mb-4 text-[#C85A32]">Why the Slowpour</p>
        <h2 className="font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">Juice-bar quality, <span className="italic">without the juice-bar line.</span></h2>
      </Reveal>
      <div className="grid auto-rows-[minmax(240px,auto)] grid-cols-1 gap-5 md:grid-cols-6 lg:grid-cols-12">
        <Reveal className="md:col-span-6 lg:col-span-7 lg:row-span-2">
          <div data-testid="bento-item-chute" className="group relative h-full min-h-[420px] overflow-hidden rounded-[28px]">
            <img src={IMG.kitchen} alt="Friends juicing together in a sunny kitchen" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#181615]/85 via-[#181615]/20 to-transparent" />
            <div className="absolute bottom-0 p-7 text-[#FDFBF7] sm:p-10">
              <Icon icon={Apple} dark />
              <h3 className="font-display text-3xl font-light sm:text-4xl">Drop in whole fruit.</h3>
              <p className="mt-3 max-w-md text-sm text-[#F2EAE1] sm:text-base">The large-diameter self-feeding chute swallows whole apples and oranges — less prep, more pressing.</p>
            </div>
          </div>
        </Reveal>
        <Card testId="bento-item-auger" className="md:col-span-3 lg:col-span-5" delay={0.05}>
          <Icon icon={RotateCw} />
          <h3 className="font-display text-2xl">Low speed, high yield</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6E6661]">Masticating means squeezing, not spinning. Drier pulp, more juice, deeper colour in every glass.</p>
        </Card>
        <Card testId="bento-item-motor" className="md:col-span-3 lg:col-span-5" delay={0.1}>
          <Icon icon={Volume1} />
          <h3 className="font-display text-2xl">Quiet enough for 6am</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6E6661]">A slow, steady motor that won't wake the house — your morning ritual stays peaceful.</p>
        </Card>
        <Card testId="bento-item-bpa" className="md:col-span-3 lg:col-span-4" delay={0.05}>
          <Icon icon={Leaf} />
          <h3 className="font-display text-2xl">BPA-free, top to bottom</h3>
          <p className="mt-3 text-sm leading-relaxed text-[#6E6661]">Every surface your juice touches is free of Bisphenol-A.</p>
          <Droplets className="absolute -bottom-6 -right-6 text-[#F2EAE1]" size={120} />
        </Card>
        <Reveal className="md:col-span-3 lg:col-span-8" delay={0.1}>
          <div data-testid="bento-item-almanac" className="grain relative flex h-full flex-col justify-between gap-8 overflow-hidden rounded-[28px] bg-[#C85A32] p-7 text-[#FDFBF7] sm:flex-row sm:items-end sm:p-9">
            <div>
              <Icon icon={BookOpen} dark />
              <h3 className="font-display text-3xl font-light">The Recipe Almanac — on us.</h3>
              <p className="mt-3 max-w-md text-sm text-[#FBE9DF]">12 LA-inspired cold-press recipes, delivered as a beautiful PDF the moment your order is confirmed.</p>
            </div>
            <span className="font-display shrink-0 text-6xl italic text-[#FBE9DF] sm:text-7xl">Free</span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
